import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { ContentLocaleService } from '@app/shared/services/content-locale.service';

const PLUGIN_SCOPES = ['coyo', 'iframe'];

function isLocalizedContentRead(request: HttpRequest<unknown>): boolean {
  if (request.method !== 'GET' || /^https?:\/\//i.test(request.url)) {
    return false;
  }

  const path = request.url.split('?')[0].replace(/^\/+|\/+$/g, '');
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) {
    return false;
  }

  if (segments[0] === 'episodes') {
    if (segments.length <= 2) {
      return true;
    }
    if (segments.length === 3) {
      return PLUGIN_SCOPES.includes(segments[2]);
    }
    return segments.length === 4 && segments[1] === 'by-token' && PLUGIN_SCOPES.includes(segments[3]);
  }

  if (segments[0] === 'podcasts') {
    if (segments.length <= 2) {
      return true;
    }
    if (segments.length === 3) {
      return segments[2] === 'episodes' || PLUGIN_SCOPES.includes(segments[1]);
    }
    return segments.length === 4 && PLUGIN_SCOPES.includes(segments[1]) && segments[3] === 'episodes';
  }

  return false;
}

/**
 * Marks a read as coming from the listener app. The API serves administrators the source
 * content unless told otherwise (the admin console edits it); an administrator opening the
 * listener app is a listener and must see translations and the available languages like
 * everyone else.
 */
export const LISTENER_AUDIENCE_HEADER = 'X-Hypecast-Content-Audience';
export const LISTENER_AUDIENCE_VALUE = 'listener';

export function localizeRequest(request: HttpRequest<unknown>, locale: string | null): HttpRequest<unknown> {
  if (!isLocalizedContentRead(request)) {
    return request;
  }

  let localizedRequest = request;
  if (!request.headers.has(LISTENER_AUDIENCE_HEADER)) {
    localizedRequest = localizedRequest.clone({
      headers: localizedRequest.headers.set(LISTENER_AUDIENCE_HEADER, LISTENER_AUDIENCE_VALUE),
    });
  }

  if (!locale) {
    return localizedRequest;
  }

  if (!request.params.has('locale')) {
    localizedRequest = localizedRequest.clone({
      params: localizedRequest.params.set('locale', locale),
    });
  }

  if (locale !== 'source' && !localizedRequest.headers.has('Accept-Language')) {
    localizedRequest = localizedRequest.clone({
      headers: localizedRequest.headers.set('Accept-Language', locale),
    });
  }

  return localizedRequest;
}

export const contentLocaleInterceptorFn: HttpInterceptorFn = (request, next) => {
  const locale = inject(ContentLocaleService).getExplicitLocale();
  return next(localizeRequest(request, locale));
};
